import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { MagnifyingGlass } from "@phosphor-icons/react";

const SOURCES = [
  { key: "job", label: "Jobs", url: "/jobs", pick: (d) => d.jobs || [] },
  { key: "emergency_job", label: "Emergency", url: "/emergency-jobs", pick: (d) => (Array.isArray(d) ? d : d.emergency_jobs || d.jobs || []) },
  { key: "job_request", label: "Requests", url: "/job-requests", pick: (d) => (Array.isArray(d) ? d : d.requests || d.job_requests || d.items || []) },
];

/** Search jobs / emergency jobs / job requests and pick one to link (used by RAMS, permits and toolbox talks). */
export default function EntityPicker({ onPick, compact = false }) {
  const [source, setSource] = useState("job");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const src = SOURCES.find(s => s.key === source);
    const t = setTimeout(() => {
      setLoading(true);
      API.get(src.url, { params: { search: q || undefined, limit: 20, include_sub_jobs: source === "job" ? true : undefined } })
        .then(({ data }) => {
          let list = src.pick(data);
          if (q) { const needle = q.toLowerCase(); list = list.filter(x => `${x.title} ${x.reference} ${x.property_address || ""} ${x.client_company || x.client_name || ""}`.toLowerCase().includes(needle)); }
          setItems(list.slice(0, 20));
        })
        .catch(() => setItems([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [source, q]);

  return (
    <div className="space-y-2" data-testid="entity-picker">
      <div className="flex gap-1">
        {SOURCES.map(s => (
          <button key={s.key} type="button" onClick={() => setSource(s.key)} data-testid={`entity-picker-src-${s.key}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${source === s.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{s.label}</button>
        ))}
      </div>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, reference, address or client…" className="pl-8 h-8 text-xs" data-testid="entity-picker-search" />
      </div>
      <div className={`border border-border rounded-md divide-y divide-border overflow-y-auto ${compact ? "max-h-48" : "max-h-72"}`}>
        {loading && items.length === 0 && <p className="text-xs text-muted-foreground p-3">Searching…</p>}
        {!loading && items.length === 0 && <p className="text-xs text-muted-foreground p-3">Nothing found.</p>}
        {items.map(x => (
          <button key={x.id} type="button" onClick={() => onPick({ entity_type: source, entity_id: x.id, title: x.title, reference: x.reference })}
            className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors" data-testid={`entity-picker-item-${x.id}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold truncate flex-1">{x.title}</span>
              {x.reference && <Badge variant="outline" className="text-[10px]">{x.reference}</Badge>}
            </div>
            <p className="text-[11px] text-muted-foreground truncate">{[x.client_company || x.client_name, x.property_address || x.postcode].filter(Boolean).join(" · ")}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
